import {
  Children,
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

import Stepper from 'components/@step-flow/Stepper';

import { StepType } from './types';

export interface MultiStepContext<TValues extends object> {
  currentStep: StepType;
  back: (payload?: Partial<TValues>) => void;
  proceed: (payload?: Partial<TValues>) => void;
  to: (step: StepType, payload?: Partial<TValues>) => void;
  totalSteps: number;
  values?: TValues;
}

export const multistepContext = createContext<MultiStepContext<object>>({
  currentStep: 0,
  back: () => {},
  proceed: () => {},
  to: () => {},
  totalSteps: 0,
  values: undefined,
});

const { Provider } = multistepContext;

interface Props<TValues> {
  children: ReactNode | Array<ReactNode>;
  initialStep?: StepType;
  initialValues?: TValues;
  initialStepsDisabled?: Array<StepType>;
  hideStepper?: boolean;
  onStepChange?: (step: StepType, payload: Partial<TValues>) => void;
}

const MultiStepProvider = <TValues extends object>({
  children,
  initialValues = {} as TValues,
  initialStep = 0,
  hideStepper,
  onStepChange,
}: Props<TValues>) => {
  const steps = Children.toArray(children);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [values, setValues] = useState<TValues>(initialValues);

  const totalSteps = steps.length;
  const stepComponent = useMemo(() => {
    const stepIndex =
      currentStep - 1 < totalSteps ? currentStep - 1 : totalSteps - 1;
    return steps[stepIndex];
  }, [currentStep, steps, totalSteps]);

  const handleProceed = useCallback(
    (payload: Partial<TValues> = {}) => {
      const newIndex = currentStep < totalSteps ? currentStep + 1 : currentStep;
      const newValues = { ...values, ...payload };
      setCurrentStep(newIndex);
      setValues(newValues);
      onStepChange?.(newIndex - 1, payload);
    },
    [currentStep, onStepChange, totalSteps, values],
  );

  const handleBack = useCallback(
    (payload: Partial<TValues> = {}) => {
      if (currentStep <= 1) return;
      const newIndex = currentStep - 1;
      setCurrentStep(newIndex);
      setValues((values) => ({ ...values, ...payload }));
      onStepChange?.(newIndex - 1, payload);
    },
    [currentStep, onStepChange],
  );

  const handleTo = useCallback(
    (step: StepType, payload: Partial<TValues> = {}) => {
      if (step < 0 || step > totalSteps) return;
      setCurrentStep(step);
      setValues((values) => ({ ...values, ...payload }));
      onStepChange?.(step - 1, payload);
    },
    [onStepChange, totalSteps],
  );

  const providerValues = useMemo(
    () => ({
      values,
      currentStep: currentStep,
      totalSteps,
      proceed: handleProceed,
      back: handleBack,
      to: handleTo,
    }),
    [currentStep, handleBack, handleProceed, handleTo, totalSteps, values],
  );

  return (
    <Provider value={providerValues}>
      {!hideStepper && (
        <Stepper
          currentStep={providerValues.currentStep}
          totalSteps={providerValues.totalSteps}
        />
      )}

      {stepComponent}
    </Provider>
  );
};

export default MultiStepProvider;
